/**
 * Camera acquisition and lifecycle.
 *
 * Owns exactly one MediaStream at a time and guarantees that every track is stopped before
 * a new one is requested, which is what prevents the "camera stays on / device busy" class
 * of bug on mobile Safari and Android Chrome.
 *
 * Nothing here knows about landmarks, rendering or audio.
 */

import type { AppErrorInfo } from '../app/AppStateMachine';
import type { FacingMode } from '../app/settings';

export interface CameraStartOptions {
  facingMode: FacingMode;
  /** Preferred capture height; width follows from an ideal 16:9 request. */
  height: number;
  /** Preferred frame rate; the browser is free to ignore it. */
  frameRate?: number;
}

export interface CameraStatus {
  active: boolean;
  facingMode: FacingMode;
  width: number;
  height: number;
  frameRate: number;
  deviceId: string | null;
  label: string;
}

export interface CameraEvents {
  /** The stream ended unexpectedly (device unplugged, OS took the camera, tab evicted). */
  onInterrupted(info: AppErrorInfo): void;
  /** New track settings became available (also fires after an orientation-driven resize). */
  onSettingsChanged(status: CameraStatus): void;
}

export class CameraError extends Error {
  readonly info: AppErrorInfo;

  constructor(info: AppErrorInfo) {
    super(info.message);
    this.name = 'CameraError';
    this.info = info;
  }
}

function toCameraError(error: unknown, requestedFacing: FacingMode): CameraError {
  const name =
    typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : '';
  const detail = error instanceof Error ? error.message : String(error);

  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new CameraError({
        kind: 'camera-permission',
        message:
          'Camera access was blocked. Allow the camera for this site in your browser settings, then try again.',
        recoverable: true,
        cause: error,
      });
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return new CameraError({
        kind: 'camera-unavailable',
        message: 'No camera was found on this device.',
        recoverable: false,
        cause: error,
      });
    case 'NotReadableError':
    case 'TrackStartError':
      return new CameraError({
        kind: 'camera-unavailable',
        message: 'The camera is already in use by another application.',
        recoverable: true,
        cause: error,
      });
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return new CameraError({
        kind: 'camera-facing-unavailable',
        message: `No camera matched the requested "${requestedFacing}" facing mode.`,
        recoverable: true,
        cause: error,
      });
    default:
      return new CameraError({
        kind: 'camera-unavailable',
        message: detail || 'The camera could not be started.',
        recoverable: true,
        cause: error,
      });
  }
}

/** True when the platform exposes the APIs we need at all. */
export function isCameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof globalThis.MediaStream === 'function'
  );
}

/** Secure-context check with the usual localhost exemption already handled by the browser. */
export function isSecureContextForCamera(): boolean {
  return typeof globalThis.isSecureContext === 'boolean' ? globalThis.isSecureContext : true;
}

export class CameraController {
  private stream: MediaStream | null = null;

  private track: MediaStreamVideoTrack | null = null;

  private readonly video: HTMLVideoElement;

  private readonly events: CameraEvents;

  private currentFacing: FacingMode = 'user';

  private multipleCameras = false;

  private enumerated = false;

  private readonly handleTrackEnded = (): void => {
    this.events.onInterrupted({
      kind: 'camera-interrupted',
      message: 'The camera stream stopped. Reconnect the camera or try again.',
      recoverable: true,
    });
  };

  private readonly handleTrackMute = (): void => {
    // A muted track is usually a transient OS-level interruption (a call, another app).
    // We surface it as recoverable so the UI can offer a retry without tearing everything down.
    this.events.onInterrupted({
      kind: 'camera-interrupted',
      message: 'The camera was interrupted by the system.',
      recoverable: true,
    });
  };

  constructor(video: HTMLVideoElement, events: CameraEvents) {
    this.video = video;
    this.events = events;
  }

  get status(): CameraStatus {
    const settings = this.track?.getSettings() ?? {};
    return {
      active: this.track !== null && this.track.readyState === 'live',
      facingMode: this.currentFacing,
      width: this.video.videoWidth || settings.width || 0,
      height: this.video.videoHeight || settings.height || 0,
      frameRate: settings.frameRate ?? 0,
      deviceId: settings.deviceId ?? null,
      label: this.track?.label ?? '',
    };
  }

  get videoTrack(): MediaStreamVideoTrack | null {
    return this.track;
  }

  get facingMode(): FacingMode {
    return this.currentFacing;
  }

  /** Whether a Switch camera control should be offered. */
  get canSwitch(): boolean {
    return this.multipleCameras;
  }

  /**
   * Acquire a stream and bind it to the video element.
   * Falls back to an unconstrained request when the requested facing mode is unavailable,
   * so a desktop with a single webcam still works when the app asks for "environment".
   */
  async start(options: CameraStartOptions): Promise<CameraStatus> {
    if (!isCameraSupported()) {
      throw new CameraError({
        kind: 'camera-unavailable',
        message: 'This browser does not support camera capture (getUserMedia is unavailable).',
        recoverable: false,
      });
    }
    if (!isSecureContextForCamera()) {
      throw new CameraError({
        kind: 'camera-unavailable',
        message:
          'Camera access requires a secure context. Open the app over HTTPS or on localhost.',
        recoverable: false,
      });
    }

    this.stop();

    const base: MediaTrackConstraints = {
      height: { ideal: options.height },
      width: { ideal: Math.round((options.height * 16) / 9) },
      frameRate: { ideal: options.frameRate ?? 30, max: 60 },
    };

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { ...base, facingMode: { ideal: options.facingMode } },
      });
    } catch (error) {
      const cameraError = toCameraError(error, options.facingMode);
      if (cameraError.info.kind !== 'camera-facing-unavailable') throw cameraError;
      // Retry once without the facing constraint.
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: base });
      } catch (retryError) {
        throw toCameraError(retryError, options.facingMode);
      }
    }

    const [track] = stream.getVideoTracks();
    if (!track) {
      stream.getTracks().forEach((t) => t.stop());
      throw new CameraError({
        kind: 'camera-unavailable',
        message: 'The camera returned a stream with no video track.',
        recoverable: true,
      });
    }

    this.stream = stream;
    this.track = track;
    this.currentFacing = resolveFacing(track, options.facingMode);

    track.addEventListener('ended', this.handleTrackEnded);
    track.addEventListener('mute', this.handleTrackMute);

    this.video.srcObject = stream;
    this.video.muted = true;
    this.video.playsInline = true;
    await this.playVideo();
    await this.waitForMetadata();

    void this.refreshDeviceList();
    const status = this.status;
    this.events.onSettingsChanged(status);
    return status;
  }

  private async playVideo(): Promise<void> {
    try {
      await this.video.play();
    } catch (error) {
      // Playback can be rejected before a gesture on some browsers; the stream is still
      // attached and `requestVideoFrameCallback` will begin once the element plays.
      console.warn('[camera] video.play() was rejected', error);
    }
  }

  private waitForMetadata(timeoutMs = 8000): Promise<void> {
    if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && this.video.videoWidth > 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const video = this.video;
      const cleanup = (): void => {
        video.removeEventListener('loadedmetadata', onReady);
        video.removeEventListener('loadeddata', onReady);
        clearTimeout(timer);
      };
      const onReady = (): void => {
        if (video.videoWidth === 0) return;
        cleanup();
        resolve();
      };
      const timer = setTimeout(() => {
        cleanup();
        if (video.videoWidth > 0) {
          resolve();
          return;
        }
        reject(
          new CameraError({
            kind: 'camera-unavailable',
            message: 'The camera stream did not produce any frames.',
            recoverable: true,
          }),
        );
      }, timeoutMs);
      video.addEventListener('loadedmetadata', onReady);
      video.addEventListener('loadeddata', onReady);
    });
  }

  /** Populate `canSwitch`. Labels are empty before permission is granted, so call it after. */
  async refreshDeviceList(): Promise<void> {
    if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      this.multipleCameras = cameras.length > 1;
      this.enumerated = true;
    } catch {
      this.multipleCameras = this.enumerated ? this.multipleCameras : false;
    }
  }

  /** Flip between front and rear cameras, restoring the previous one if the flip fails. */
  async switchCamera(height: number): Promise<CameraStatus> {
    const next: FacingMode = this.currentFacing === 'user' ? 'environment' : 'user';
    const previous = this.currentFacing;
    try {
      return await this.start({ facingMode: next, height });
    } catch (error) {
      try {
        return await this.start({ facingMode: previous, height });
      } catch {
        throw error;
      }
    }
  }

  /** Stop all tracks and detach the element. Always safe to call. */
  stop(): void {
    const track = this.track;
    if (track) {
      track.removeEventListener('ended', this.handleTrackEnded);
      track.removeEventListener('mute', this.handleTrackMute);
    }
    if (this.stream) {
      for (const t of this.stream.getTracks()) {
        try {
          t.stop();
        } catch {
          // Already stopped.
        }
      }
    }
    this.stream = null;
    this.track = null;
    try {
      this.video.pause();
    } catch {
      // Ignored; the element may not be attached.
    }
    this.video.srcObject = null;
  }

  dispose(): void {
    this.stop();
  }
}

/**
 * The `facingMode` a track actually delivered.
 *
 * Desktop browsers commonly omit `facingMode` entirely, in which case we keep the value we
 * asked for so the mirroring default stays sensible (webcams are user-facing).
 */
function resolveFacing(track: MediaStreamTrack, requested: FacingMode): FacingMode {
  const settings = track.getSettings();
  const reported = settings.facingMode;
  if (reported === 'user' || reported === 'environment') return reported;
  return requested;
}

/** Video tracks are plain MediaStreamTracks; alias for readability. */
export type MediaStreamVideoTrack = MediaStreamTrack;
